import { Controller } from "@hotwired/stimulus";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { buildMentionSuggestion } from "../helpers/mention_suggestion";
import { replaceAnchorsWithHrefs } from "../helpers/paste_urls";

export default class extends Controller {
  static targets = ["editor", "hiddenField", "mentionIdsField"];
  static values = {
    communityId: String,
    autofocus: { type: Boolean, default: false },
    content: { type: String, default: "" },
    placeholder: { type: String, default: "" },
  };

  connect() {
    this.initializeEditor();
    this.element.addEventListener(
      "comment-editor:insert-mention",
      this.handleInsertMention,
    );
  }

  disconnect() {
    this.editor?.destroy();
    this.element.removeEventListener(
      "comment-editor:insert-mention",
      this.handleInsertMention,
    );
  }

  // Replace an existing leading mention (+ its trailing space) with the new
  // one, or insert at the start if no leading mention exists. Position 1 is
  // inside the first paragraph in ProseMirror.
  handleInsertMention = (event) => {
    if (!this.editor) return;
    const label = event.detail.displayName.replace(/\s+/g, "\u00A0");
    const id = event.detail.userId ?? null;

    const firstParagraph = this.editor.state.doc.firstChild;
    const firstNode = firstParagraph?.firstChild;
    const from = 1;
    let to = from;

    if (firstNode?.type.name === "mention") {
      to += firstNode.nodeSize;
      const nextNode = firstParagraph.maybeChild(1);
      if (nextNode?.isText && nextNode.text.startsWith(" ")) {
        to += 1;
      }
    }

    this.editor
      .chain()
      .insertContentAt({ from, to }, [
        { type: "mention", attrs: { id, label } },
        { type: "text", text: " " },
      ])
      .run();
  };

  initializeEditor() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          // Comments are stored as plain text, so a link's href never survives
          // submission — only its visible text does. linkOnPaste would turn a
          // pasted URL into a link on the selected word and drop the URL with
          // it, the same loss transformPastedHTML repairs for pasted anchors.
          // Autolink stays on: it only ever links text that is already the URL.
          link: { linkOnPaste: false },
          bold: false,
          italic: false,
          strike: false,
          code: false,
          codeBlock: false,
          heading: false,
          blockquote: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          horizontalRule: false,
        }),
        Placeholder.configure({
          placeholder: this.placeholderValue,
        }),
        Mention.configure({
          HTMLAttributes: { class: "mention" },
          renderText({ node }) {
            return `@${node.attrs.label ?? node.attrs.id}`;
          },
          renderHTML({ options, node }) {
            return [
              "span",
              options.HTMLAttributes,
              `@${node.attrs.label ?? node.attrs.id}`,
            ];
          },
          suggestion: buildMentionSuggestion({
            communityId: this.communityIdValue,
            appendTo: () => this.element.closest("dialog") || document.body,
          }),
        }),
      ],
      content: "",
      onUpdate: ({ editor }) => {
        this.syncToHiddenField(editor);
        this.syncMentionIds(editor);
        this.dispatchInputEvent();
      },
      editorProps: {
        transformPastedHTML: replaceAnchorsWithHrefs,
        handleClick: (view, pos, event) => {
          const link = event.target.closest("a");
          if (link) {
            event.preventDefault();
            return true; // tells ProseMirror we handled it
          }
          return false;
        },
      },
    });

    this.setInitialContent();

    if (this.autofocusValue) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.editor.commands.focus("end");
          this.editorTarget.scrollIntoView({ block: "nearest" });
        });
      });
    }
  }

  setInitialContent() {
    if (!this.contentValue) return;

    // Check if content starts with a mention pattern (@Username )
    // The content from the controller uses \u00A0 between name parts: "@Hello\u00A0World "
    const mentionMatch = this.contentValue.match(/^@(.+?) $/);
    if (mentionMatch) {
      // Keep \u00A0 in label — renderText outputs them so getText() preserves
      // them in the hidden field, and MENTION_PATTERN relies on \u00A0 to
      // treat multi-word names as a single mention.
      const label = mentionMatch[1];
      this.editor.commands.insertContent([
        {
          type: "mention",
          attrs: { id: null, label },
        },
        { type: "text", text: " " },
      ]);
    } else {
      this.editor.commands.setContent(this.contentValue);
    }

    this.syncToHiddenField(this.editor);
    this.syncMentionIds(this.editor);
  }

  syncToHiddenField(editor) {
    if (!this.hasHiddenFieldTarget) return;
    const text = editor.getText({ blockSeparator: "\n" });
    this.hiddenFieldTarget.value = text;
  }

  // Collect the user ids of every mention the author actually selected from the
  // dropdown. These ids are the single source of truth for who gets notified —
  // the backend resolves recipients by id and never re-matches the @name (which
  // would notify every account sharing that name). A mention without an id (e.g.
  // the auto-inserted reply mention, or one reconstructed from plain text when
  // editing) is intentionally not a notification target.
  syncMentionIds(editor) {
    if (!this.hasMentionIdsFieldTarget) return;
    const ids = [];
    editor.state.doc.descendants((node) => {
      if (node.type.name === "mention" && node.attrs.id != null) {
        ids.push(node.attrs.id);
      }
    });
    this.mentionIdsFieldTarget.value = [...new Set(ids)].join(",");
  }

  dispatchInputEvent() {
    // Notify feed_input_controller about content changes
    this.element.dispatchEvent(
      new CustomEvent("comment-editor:change", { bubbles: true }),
    );
  }

  // Called by feed_input_controller after form reset
  clear() {
    this.editor?.commands.clearContent();
    this.syncToHiddenField(this.editor);
    this.syncMentionIds(this.editor);
  }

  isEmpty() {
    return !this.editor || this.editor.getText().trim().length === 0;
  }
}
